import React, { useEffect, useMemo, useState } from 'react';
import moment from "moment-timezone";
import html2canvas from 'html2canvas';
import { PDFDownloadLink } from '@react-pdf/renderer';
import { Modal, Button } from 'react-bootstrap';
import { ShareFill } from 'react-bootstrap-icons';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import HighchartsExporting from 'highcharts/highcharts-more'
import { getCardioHistory } from '../api';
import config from '../config';
import { useSelector } from 'react-redux';
import ErrorIcon from '../assets/SVG/icons/error-icon.svg';
import '../styles/cardiohistory.css';
import CardioHistoryPDF from './CardioHistoryPDF';
HighchartsExporting(Highcharts);

function CardioHistory() {
    const user = useSelector(state =>  state.user);

    const [pdfUrl, setPdfUrl] = useState(null);
    const [open, setOpen] = useState(false);
    const [isProcess, setProcess] = useState(false);
    const [isReady, setReady] = useState(false);
    const [pdfData, setPdfData] = useState(null);
    
    const [isLoading, setLoading] = useState(false);
    const [sign, setSign] = useState("hr");
    const [currentTab, setCurrentTab] = useState("daily");
    const [testResults, setTestResults] = useState([]);
    const [seriesData, setSeriesData] = useState([]);
    const [categories, setCategories] = useState([]);
    const [max, setMax] = useState(67);
    const [title, setTitle] = useState("HEART RATE")
    const [unit, setUnit] = useState("bpm");
    const [bpData, setBPData] = useState({
        diastolic: [],
        systolic: []
    })
    const [dateRange, setDateRange] = useState("")
    const [valueRange, setValueRange] = useState([])

    const onChangeSign = (e) => {
        const value = e.target.value;
        let newTitle;
        let newUnit;
        switch (value) {
            case "bp":
                newTitle = "BLOOD PRESSURE";
                newUnit = "mmHg";
                break;
            case "rr":
                newTitle = "RESPIRATORY RATE";
                newUnit = "brpm";
                break;
            case "spo2":
                newTitle = "OXYGEN SPO2";
                newUnit = "%";
                break;
            case "hrv":
                newTitle = "HEART RATE VARIABILITY";
                newUnit = "ms";
                break;
            default:
                newTitle = "HEART RATE";
                newUnit = "bpm";
                break;
        }
        setSign(value)
        setTitle(newTitle)
        setUnit(newUnit)
    }
    const getTestResultsByDate = async () => {
        if(currentTab === "yearly") {
            return testResults.sort(function(a,b){
                return new Date(a.createdAt) - new Date(b.createdAt);
            });
        }

        let from;
        switch (currentTab) {
            case "6M":
                from = moment().clone().subtract(6,'months').startOf('day').tz(user.timezone || config.defaultTimezone).utc();
                break;
            case "monthly":
                from = moment().clone().subtract(1,'months').startOf('day').tz(user.timezone || config.defaultTimezone).utc();
                break;
            case "weekly":
                from = moment().clone().subtract(1,'weeks').add(1, "days").startOf('day').tz(user.timezone || config.defaultTimezone).utc();
                break;        
            default:
                from = moment().clone().startOf('day').tz(user.timezone || config.defaultTimezone).utc();
                break;
        }
        let filteredTests = testResults.filter((item) => {
            const createdAt = moment(item.createdAt).tz(user.timezone || config.defaultTimezone).utc();
            const diff = createdAt.diff(from);
            if(diff > 0) return item;
        })
        filteredTests.sort(function(a,b){
            return new Date(a.createdAt) - new Date(b.createdAt);
        });
        return filteredTests;
    }
    const getXKey = async (createdAt) => {
        let key;
        switch (currentTab) {
            case "daily":
                key = moment(createdAt).clone().tz(user.timezone || config.defaultTimezone).format('HH:mm');
                break;
            case "weekly":
                key = moment(createdAt).clone().tz(user.timezone || config.defaultTimezone).format('ddd');
                break;
            case "monthly":
                key = moment(createdAt).clone().tz(user.timezone || config.defaultTimezone).format('MMM D');
                break;        
            default:
                key = moment(createdAt).clone().tz(user.timezone || config.defaultTimezone).format('MMM');
                break;
        }
        return key;
    }
    const getDateRange = async () => {
        if(currentTab === "daily") return "Today"
        const current = moment().clone().endOf('day').tz(user.timezone || config.defaultTimezone).utc()
        let range = "";
        let from = ""
        switch (currentTab) {
            case "weekly":
                from = moment().clone().subtract(1,'weeks').add(1, "days").startOf('day').tz(user.timezone || config.defaultTimezone).utc();
                break;
            case "monthly":
                from = moment().clone().subtract(1,'months').startOf('day').tz(user.timezone || config.defaultTimezone).utc();
                break;
            case "6M":
                from = moment().clone().subtract(6,'months').startOf('day').tz(user.timezone || config.defaultTimezone).utc();
                break;
            default:                
                from = moment().clone().subtract(1,'years').startOf('day').tz(user.timezone || config.defaultTimezone).utc();                
                break;
        }

        if(from.isSame(current, "month") && from.isSame(current, "year")) {
            range = `${from.format("MMM")} ${from.format("DD")} - ${current.format("DD")}, ${current.format("YYYY")}`
        } else if(from.isSame(current, "year")) {
            range = `${from.format("MMM")} ${from.format("DD")} - ${current.format("MMM")} ${current.format("DD")}, ${current.format("YYYY")}`
        } else {
            range = `${from.format("MMM")} ${from.format("DD")}, ${from.format("YYYY")} - ${current.format("MMM")} ${current.format("DD")}, ${current.format("YYYY")}`
        }
        
        return range;
    }
    const drawChart = async () => {
        let seriesData = [];
        let testValues = [];
        const testData = await getTestResultsByDate();
        const range = await getDateRange();
        setDateRange(range);

        if(testData.length > 0) {
            let preKey = await getXKey(testData[0].createdAt);
            let values = [];
            let irregular = false;
            for(let i=0; i<testData.length; i++) {
                const test = testData[i];
                let key = await getXKey(test.createdAt);
                if(test[sign]) {
                    let itemData;
                    if(sign == "hr") {
                        if(test[sign].value) {
                            if(test[sign].irregular) irregular = test[sign].irregular;
                            testValues.push( test[sign].value);
                            itemData = test[sign].value;
                        } else {
                            testValues.push(test[sign]);                        
                            itemData = test[sign];
                        }
                    } else {
                        testValues.push(test[sign]);
                        itemData = test[sign];
                    }
                    if(preKey === key) {
                        values.push(itemData)
                    } else {
                        seriesData.push({
                            key: preKey,
                            value: values,
                            irregular: irregular
                        })
                        values = [itemData]
                        preKey = key;
                        irregular = false;
                    }
                }
            }
            seriesData.push({
                key: preKey,
                value: values,
                irregular: irregular
            })
        }
        const data = seriesData;
        let series = [];
        let diastolic = [];
        let systolic = [];
        let keys = [];

        let maxValue = Math.max(...testValues);
        let minValue = Math.min(...testValues);
        let diff = maxValue - minValue;
        let more = diff < 5 ? 0.5: diff < 10 ? 1 : 2;
        let moreMax = diff > 50 ? 0: 10;
        setMax(maxValue + moreMax)

        for(let i=0; i<data.length; i++) {
            const serie = data[i].value;
            keys.push(data[i].key);
            if(currentTab === "daily" && sign !== "bp") {
                if(sign == "hr" && data[i].irregular) {
                    series.push({
                        y: serie[0],
                        irregular: true
                    })
                } else {
                    series.push(serie[0])
                }
                              
            } else {
                if(sign === "hrv") {
                    let average = serie.reduce((a, b) => a + b) / serie.length;
                    series.push(Math.round(average))
                } else if(sign === "bp") {
                    let totalD = 0;
                    let totalS = 0;
                    for(let k = 0; k < serie.length; k++) {
                        totalD += serie[k].diastolic;
                        totalS += serie[k].systolic;
                    }
                    diastolic.push(Math.round(totalD / serie.length))
                    systolic.push(Math.round(totalS / serie.length))
                } else {
                    const min = Math.min(...serie);
                    const max = Math.max(...serie)
                    if(min === max) {
                        series.push({
                            low: serie[0],
                            high: serie[0] - more,
                            custom: "same",
                            irregular: data[i].irregular
                        })
                    } else {
                        series.push({
                            low: min,
                            high: max,
                            irregular: data[i].irregular
                        })
                    }
                }
            }
        }

        if(testValues.length > 0) {
            if(sign === "bp") {
                let diastolicMin = Math.min(...diastolic);
                let diastolicMax = Math.max(...diastolic);
                let diastolicStr = diastolicMin == diastolicMax ? diastolicMin : `${diastolicMin} - ${diastolicMax}`

                let systolicMin = Math.min(...systolic);
                let systolicMax = Math.max(...systolic);
                let systolicStr = systolicMin == systolicMax ? systolicMin : `${systolicMin} - ${systolicMax}`

                setValueRange([
                    `Diastolic: ${diastolicStr}`,
                    `Systolic: ${systolicStr}`,
                ])
            } else {
                let min = 0;
                let max = 0;
                if(currentTab === "daily" || sign === "hrv") {
                    min = Math.min(...series);
                    max = Math.max(...series);
                } else {
                    let values = [];
                    for(let i=0; i<series.length; i++) {
                        let item = series[i];
                        if(item.custom && item.custom == "same") {
                            values.push(item.low);
                        } else {
                            values.push(item.low);
                            values.push(item.high);
                        }
                    }
                    min = Math.min(...values);
                    max = Math.max(...values);
                }
                let str = min == max ? min : `${min} - ${max}`
                setValueRange([str])
                
            }
        } else {
            setValueRange([])
        }
        setCategories(keys);
        setSeriesData(series);
        setBPData({
            diastolic,
            systolic
        })
    }
    const getTestResults = async () => {
        setLoading(true);
        const from = moment().clone().subtract(1,'years').startOf('day').tz(user.timezone || config.defaultTimezone).utc().format('YYYY-MM-DDTHH:mm:ss.SSSZ');
        const to = moment().clone().endOf('day').tz(user.timezone || config.defaultTimezone).utc().format('YYYY-MM-DDTHH:mm:ss.SSSZ');
        const data = await getCardioHistory(from, to);
        if(data.test_results) {
            setTestResults(data.test_results);
        } else {
            setTestResults([])
        }
        setLoading(false);
    }
    const getClasses = (tab) => {
        if(currentTab === tab) {
            return "nav-link active border-0"
        } else {
            return "nav-link border-0"
        }
    }

    useEffect(() => {
        drawChart()
    }, [testResults, sign, currentTab])
    useEffect(() => {
        getTestResults();
    }, [])

    const cardioOptions = useMemo(() => {
        let options = {
            chart: {
                type: "spline"
            },      
            title: {
                text: title
            },
            credits: {
                enabled: false
            },
            legend: {
              enabled: false
            },
            xAxis: {
                categories: categories
            },
            plotOptions: {
                spline: {
                    lineWidth: 2,
                    states: {
                        hover: {
                            enabled: true
                        }
                    },
                    dataLabels: {
                        enabled: false,
                    }
                },
                columnrange: {
                    dataLabels: {
                        enabled: false,
                    }
                }
            },
            yAxis: {
                title: {
                    text: unit
                },
                max: null
            }          
        }
        if(sign === "hrv") {
            return {
                ...options,
                chart: {
                    type: "spline"
                },
                tooltip: {
                    formatter: function() {
                        return `${title}: <b>${this.y} ${unit}</b>`;
                    }
                },
                plotOptions: {
                    spline: {
                        lineWidth: 2,
                        states: {
                            hover: {
                                enabled: true
                            }
                        },
                        dataLabels: {
                            enabled: false,
                        }
                    }
                },
                series: [{
                  color: "red",
                  pointWidth: 10,
                  pointPadding: 0.25,
                  borderRadius: 5,
                  data: seriesData
                }]
              
            } 
        }
        if(sign === "bp") {
            return {
                ...options,
                chart: {
                    type: "spline"
                },
                tooltip: {
                    formatter: function() {
                        return `${this.series.name}: <b>${this.y} ${unit}</b>`;
                    }
                },
                legend: {
                  enabled: true
                },
                plotOptions: {
                    spline: {
                        lineWidth: 2,
                        states: {
                            hover: {
                                enabled: true
                            }
                        },
                        dataLabels: {
                            enabled: false,
                        }
                    }
                },
                series: [{
                  name: 'Diastolic',
                  color: "blue",
                  pointWidth: 10,
                  pointPadding: 0.25,
                  borderRadius: 5,
                  data: bpData.diastolic
                },{
                    name: 'Systolic',
                    color: "red",
                    pointWidth: 10,
                    pointPadding: 0.25,
                    borderRadius: 5,
                    data: bpData.systolic
                  }]
              
            } 
        }
        if(sign === "hr") {
            if(currentTab === "daily") {
                return {
                    ...options,
                    chart: {
                        type: "spline"
                    },
                    tooltip: {
                        formatter: function() {
                            return `${title}: <b>${this.y} ${unit}</b><br><b>${this.point.irregular ? "Irregularity detected" : ""}</b>`;
                        }
                    },
                    plotOptions: {
                        spline: {
                            lineWidth: 0,
                            states: {
                                hover: {
                                    enabled: false
                                }
                            },
                            dataLabels: {
                                enabled: true,
                                useHTML: true,
                                allowOverlap: true,
                                formatter: function(e) {
                                    if (this.point.irregular && e.verticalAlign == "bottom") {
                                      return `<div style="transform: translateX(-50%);"><img src=${ErrorIcon} alt="error icon" /></div>`
                                    }
                                },
                                style: {
                                    zIndex: 0
                                }
                            }
                        }
                    },
                    series: [{
                      name: 'HEART RATE',
                      color: "red",
                      pointWidth: 10,
                      pointPadding: 0.25,
                      borderRadius: 5,
                      data: seriesData
                    }]
                  
                }
            } else {
                return {
                    ...options,
                    chart: {
                        type: 'columnrange'
                    },
                    yAxis: {
                        title: {
                            text: unit
                        },
                        max: max
                    },
                    tooltip: {
                        useHTML: true,
                        formatter: function() {
                            if(this.point.custom == "same") {
                                return `${title}: <b>${this.point.options.low} ${unit}</b><br><b>${this.point.irregular ? "Irregularity detected" : ""}</b>`;
                            }
                            return `${title}: <b>${this.point.options.low} - ${this.point.options.high}${unit}</b><br><b>${this.point.irregular ? "Irregularity detected" : ""}</b>`;
                        },
                        style: {
                            zIndex: 100
                        }
                    },
                    plotOptions: {
                        columnrange: {
                            dataLabels: {
                                enabled: true,
                                useHTML: true,
                                formatter: function(e) {
                                    if (this.point.irregular && e.verticalAlign == "bottom") {
                                      return `<div style="transform: translateX(-50%);"><img src=${ErrorIcon} alt="error icon" /></div>`
                                    }
                                },
                                style: {
                                    zIndex: 0
                                }
                            }
                        }
                    },
                    series: [{
                      color: "red",
                      pointWidth: 10,
                      pointPadding: 0.25,
                      borderRadius: 5,
                      data: seriesData
                    }]
                  
                }
            }
        }
        if(currentTab === "daily") {
            return {
                ...options,
                chart: {
                    type: "spline"
                },
                tooltip: {
                    formatter: function() {
                        return `${title}: <b>${this.y} ${unit}</b>`;
                    }
                },
                plotOptions: {
                    spline: {
                        lineWidth: 0,
                        states: {
                            hover: {
                                enabled: false
                            }
                        },
                        dataLabels: {
                            enabled: false,
                        }
                    }
                },
                series: [{
                  name: 'HEART RATE',
                  color: "red",
                  pointWidth: 10,
                  pointPadding: 0.25,
                  borderRadius: 5,
                  data: seriesData
                }]
              
            }
        } else {
            return {
                ...options,
                chart: {
                    type: 'columnrange'
                },
                yAxis: {
                    title: {
                        text: unit
                    },
                    max: max
                },
                tooltip: {
                    useHTML: true,
                    formatter: function() {
                        if(this.point.custom == "same") {
                            return `${title}: <b>${this.point.options.low} ${unit}</b>`;
                        }
                        return `${title}: <b>${this.point.options.low} - ${this.point.options.high}${unit}</b>`;
                    },
                },

                series: [{
                  color: "red",
                  pointWidth: 10,
                  pointPadding: 0.25,
                  borderRadius: 5,
                  data: seriesData
                }]
              
            }
        }
        
    }, [seriesData, categories, max, title, unit, bpData]);

    const getPdfData = async () => {
        setProcess(true)
        const cardioChart = document.getElementById('cardio-chart');
        const canvas = await html2canvas(cardioChart);
        const imgData = canvas.toDataURL('image/png');

        setLoading(false);
        setReady(true)
        setPdfData({
            chart: imgData,
            name: `${user.firstName} ${user.lastName}`,
            date: dateRange == "Today" ? moment().format("MMM DD YYYY") : dateRange,
            title: title,
            valueRange: valueRange,
            unit: unit
        })
    }

    const onDownloadPDF = () => {
        setOpen(true)
    };

    const handleClose = () => {
        setOpen(false)
    }

    useEffect(() => {
        if(isProcess) {
            setLoading(true);
            setOpen(false);
            if(pdfUrl) {
                const currentTimestamp = new Date().getTime();
                const link = document.createElement('a');
                link.href = pdfUrl;
                link.download = `Cardio-${sign.toUpperCase()}-${currentTimestamp}.pdf`;
                link.click();
                setProcess(false)
                setLoading(false);
                setReady(false);
                setPdfUrl(null);
            }
        }        
    }, [pdfUrl, isProcess]);

    return (
        <div className="cardio-history">
            {isLoading ? <div id="cover-spin"></div> : <></>}
            <div className='share_btn'>
                {
                    isReady && pdfData !== null && <PDFDownloadLink document={<CardioHistoryPDF data={pdfData} />} fileName="covid_result.pdf">
                        {({ blob, url, loading, error }) => setPdfUrl(url)}
                    </PDFDownloadLink>
                }
                <div data-testid="share_icon" className='share_icon rounded-circle' onClick={(e) => onDownloadPDF()}><ShareFill size={20} color='#007bff' /></div>
            </div>
            <div className='period'>
                <select className="form-control" onChange={onChangeSign} data-testid="cardio-sign">
                    <option value="hr" >HEART RATE</option>
                    <option value="rr" >RESPIRATORY RATE</option>
                    <option value="spo2" >OXYGEN SPO2</option>
                    <option value="hrv" >HEART RATE VARIABILITY</option>
                    <option value="bp" >BLOOD PRESSURE</option>
                </select>
            </div>
            <div className='content mt-5'>
                <div className='text-left mb-1'>
                    {valueRange[0] && <p className='m-0'><span className='range'>{valueRange[0]}</span> {unit}</p>}
                    {valueRange[1] && <p className='m-0'><span className='range'>{valueRange[1]}</span> {unit}</p>}
                    <p className='m-0'>{dateRange}</p>
                </div>
                <ul className="nav nav-tabs">
                    <li className="nav-item cursor-pointer" onClick={() => setCurrentTab("daily")}>
                        <p className={getClasses("daily")} aria-current="daily" data-testid="daily">Daily</p>
                    </li>
                    <li className="nav-item cursor-pointer" onClick={() => setCurrentTab("weekly")}>
                        <p className={getClasses("weekly")} aria-current="weekly" data-testid="weekly">Weekly</p>
                    </li>
                    <li className="nav-item cursor-pointer" onClick={() => setCurrentTab("monthly")}>
                        <p className={getClasses("monthly")} aria-current="monthly" data-testid="monthly">Monthly</p>
                    </li>
                    <li className="nav-item cursor-pointer" onClick={() => setCurrentTab("6M")}>
                        <p className={getClasses("6M")} aria-current="6M" data-testid="6M">6M</p>
                    </li>
                    <li className="nav-item cursor-pointer" onClick={() => setCurrentTab("yearly")}>
                        <p className={getClasses("yearly")} aria-current="yearly" data-testid="yearly">Yearly</p>
                    </li>
                </ul>
                <div className='cardio-chart' id='cardio-chart'>
                    <HighchartsReact
                        highcharts={Highcharts}
                        options={cardioOptions}
                    />
                    {((sign !== "bp" && seriesData.length === 0) || (sign === "bp" && bpData.diastolic.length === 0 && bpData.systolic.length === 0)) && <p className='nodata'>No Test Record Found</p>}
                </div>
                
            </div>

            <Modal animation={false} show={open} onHide={handleClose} centered backdrop="static" className='warning_modal' data-testid="warning_modal">
                <Modal.Header>
                    <Modal.Title>Cardio Result Sharing</Modal.Title>
                </Modal.Header>
                <Modal.Body className="body">
                    <p>I understand that by sharing this information I am disclosing personal health information to the recipient and that the information shared is no longer protected by the iSelfie application privacy and security controls.</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant='secondary' id='cancel' data-testid="cancel" onClick={handleClose}>Cancel</Button>
                    <Button variant='primary' id='continue' data-testid="continue" onClick={getPdfData}>Share</Button>
                </Modal.Footer>
            </Modal>
        </div>
    )
}

export default CardioHistory;